import { useState, useEffect } from 'react';
import { ButtonFilterPeriod } from '@/components/custom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input, Label, Button } from '@/components/ui';
import { CoinPackageNameEnum, PaymentPlatformEnum } from '@/enums';
import { IPeriod } from '@/interfaces';
import { useQueryParams } from '@/hooks';
import dayjs from 'dayjs';

interface PaymentsToolbarProps {
    disabled?: boolean;
}

interface DraftFilters {
    provider: string;
    packageName: string;
    minPrice: string;
    maxPrice: string;
    period?: IPeriod;
}

const PaymentsToolbar = ({ disabled }: PaymentsToolbarProps) => {
    const { urlSearchParams, updateSearchParams, setValues } = useQueryParams([]);

    // Read applied filters from URL (these are what's currently active)
    const appliedProvider = urlSearchParams.get('provider') ?? '';
    const appliedPackageName = urlSearchParams.get('packageName') ?? '';
    const appliedMinPrice = urlSearchParams.get('minPrice') ?? '';
    const appliedMaxPrice = urlSearchParams.get('maxPrice') ?? '';
    const appliedStartDate = urlSearchParams.get('startDate');
    const appliedEndDate = urlSearchParams.get('endDate');

    // Local state for draft filters (what user is currently editing)
    const [draftFilters, setDraftFilters] = useState<DraftFilters>({
        provider: appliedProvider,
        packageName: appliedPackageName,
        minPrice: appliedMinPrice,
        maxPrice: appliedMaxPrice,
        period: {
            from: appliedStartDate ? new Date(appliedStartDate) : undefined,
            to: appliedEndDate ? new Date(appliedEndDate) : undefined,
        },
    });

    // Sync draft filters when URL changes externally (e.g., clear filters)
    useEffect(() => {
        setDraftFilters({
            provider: appliedProvider,
            packageName: appliedPackageName,
            minPrice: appliedMinPrice,
            maxPrice: appliedMaxPrice,
            period: {
                from: appliedStartDate ? new Date(appliedStartDate) : undefined,
                to: appliedEndDate ? new Date(appliedEndDate) : undefined,
            },
        });
    }, [appliedProvider, appliedPackageName, appliedMinPrice, appliedMaxPrice, appliedStartDate, appliedEndDate]);

    const handleApplyFilters = () => {
        updateSearchParams(prev => {
            // Clear existing filter params
            prev.delete('provider');
            prev.delete('packageName');
            prev.delete('minPrice');
            prev.delete('maxPrice');
            prev.delete('startDate');
            prev.delete('endDate');

            // Apply new filters
            if (draftFilters.provider) {
                prev.set('provider', draftFilters.provider);
            }
            if (draftFilters.packageName) {
                prev.set('packageName', draftFilters.packageName);
            }
            if (draftFilters.minPrice) {
                const minPriceNum = parseFloat(draftFilters.minPrice);
                if (!isNaN(minPriceNum) && minPriceNum >= 0) {
                    prev.set('minPrice', String(minPriceNum));
                }
            }
            if (draftFilters.maxPrice) {
                const maxPriceNum = parseFloat(draftFilters.maxPrice);
                if (!isNaN(maxPriceNum) && maxPriceNum >= 0) {
                    prev.set('maxPrice', String(maxPriceNum));
                }
            }
            if (draftFilters.period?.from) {
                prev.set('startDate', dayjs(draftFilters.period.from).startOf('day').toISOString());
            }
            if (draftFilters.period?.to) {
                prev.set('endDate', dayjs(draftFilters.period.to).endOf('day').toISOString());
            }

            // Reset to page 1 when filters are applied
            prev.set('paymentPage', '1');

            return prev;
        });
    };

    const handleClearFilters = () => {
        setDraftFilters({
            provider: '',
            packageName: '',
            minPrice: '',
            maxPrice: '',
            period: undefined,
        });
        setValues({
            paymentPage: '1',
        });
    };

    const hasActiveFilters = 
        appliedProvider || 
        appliedPackageName || 
        appliedMinPrice || 
        appliedMaxPrice || 
        appliedStartDate || 
        appliedEndDate;

    const hasDraftChanges = 
        draftFilters.provider !== appliedProvider ||
        draftFilters.packageName !== appliedPackageName ||
        draftFilters.minPrice !== appliedMinPrice ||
        draftFilters.maxPrice !== appliedMaxPrice ||
        (draftFilters.period?.from?.toISOString() !== (appliedStartDate ? new Date(appliedStartDate).toISOString() : undefined)) ||
        (draftFilters.period?.to?.toISOString() !== (appliedEndDate ? new Date(appliedEndDate).toISOString() : undefined));

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="flex flex-wrap gap-3 items-center">
                    {/* Provider filter */}
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Fournisseur</Label>
                        <Select
                            value={draftFilters.provider || 'ALL'}
                            onValueChange={(value) =>
                                setDraftFilters(prev => ({
                                    ...prev,
                                    provider: value === 'ALL' ? '' : value,
                                }))
                            }
                            disabled={disabled}
                        >
                            <SelectTrigger className="h-8 min-w-[150px] text-xs">
                                <SelectValue placeholder="Tous les fournisseurs" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">Tous</SelectItem>
                                {Object.values(PaymentPlatformEnum).map((value) => (
                                    <SelectItem key={value} value={value}>
                                        {value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Package filter */}
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Package</Label>
                        <Select
                            value={draftFilters.packageName || 'ALL'}
                            onValueChange={(value) =>
                                setDraftFilters(prev => ({
                                    ...prev,
                                    packageName: value === 'ALL' ? '' : value,
                                }))
                            }
                            disabled={disabled}
                        >
                            <SelectTrigger className="h-8 min-w-[150px] text-xs">
                                <SelectValue placeholder="Tous les packages" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">Tous</SelectItem>
                                {Object.values(CoinPackageNameEnum).map((value) => (
                                    <SelectItem key={value} value={value}>
                                        {value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Min Price filter */}
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Prix min (€)</Label>
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="h-8 w-24 text-xs"
                            placeholder="0.00"
                            value={draftFilters.minPrice}
                            onChange={(e) =>
                                setDraftFilters(prev => ({
                                    ...prev,
                                    minPrice: e.target.value,
                                }))
                            }
                            disabled={disabled}
                        />
                    </div>

                    {/* Max Price filter */}
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Prix max (€)</Label>
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="h-8 w-24 text-xs"
                            placeholder="0.00"
                            value={draftFilters.maxPrice}
                            onChange={(e) =>
                                setDraftFilters(prev => ({
                                    ...prev,
                                    maxPrice: e.target.value,
                                }))
                            }
                            disabled={disabled}
                        />
                    </div>

                    {/* Date range filter */}
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Période</Label>
                        <div>
                            <ButtonFilterPeriod
                                text="Filtrer par période"
                                value={draftFilters.period}
                                onChange={(period) =>
                                    setDraftFilters(prev => ({
                                        ...prev,
                                        period,
                                    }))
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleApplyFilters}
                        disabled={disabled || !hasDraftChanges}
                        size="sm"
                        className="h-8 text-xs"
                    >
                        Appliquer
                    </Button>
                    {(hasActiveFilters || hasDraftChanges) && (
                        <Button
                            onClick={handleClearFilters}
                            disabled={disabled}
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                        >
                            Réinitialiser
                        </Button>
                    )}
                </div>
            </div>

            {/* Show active filters indicator */}
            {hasActiveFilters && (
                <div className="text-xs text-muted-foreground">
                    Filtres actifs: {[
                        appliedProvider && `Fournisseur: ${appliedProvider}`,
                        appliedPackageName && `Package: ${appliedPackageName}`,
                        appliedMinPrice && `Prix min: €${parseFloat(appliedMinPrice).toFixed(2)}`,
                        appliedMaxPrice && `Prix max: €${parseFloat(appliedMaxPrice).toFixed(2)}`,
                        (appliedStartDate || appliedEndDate) && 'Période',
                    ].filter(Boolean).join(' • ')}
                </div>
            )}
        </div>
    );
};

export default PaymentsToolbar;
